/**
 * 成员管理 API
 *
 * @description 提供团队成员相关的查询接口
 */

import { request } from '@umijs/max';

/**
 * 团队成员信息
 */
export interface Member {
  /** 成员 UUID */
  uuid: string;
  /** 成员姓名 */
  name?: string;
  /** 显示名称 */
  displayName?: string;
  /** 职位 */
  position?: string;
  /** 邮箱 */
  email?: string;
  /** 状态 */
  status?: string;
  userUuid: string;
  departmentId?: string;
  mainDepartmentId?: string;
  departmentUuid?: string;
  deptIds?: string[];
  orgs?: Array<{ id?: string; nodeId?: string; name?: string }>;
}

/**
 * 成员列表条目
 */
export interface MemberWithProfile {
  /** 成员主体信息 */
  member: Member;
  /** 展示名称 */
  displayName?: string;
  /** 头像地址 */
  avatarUrl?: string | null;
}

/**
 * 成员列表查询参数
 */
export interface MemberListParams {
  /** 页码（兼容旧参数） */
  pageNo?: number;
  /** 页码 */
  page?: number;
  /** 单页数量（兼容旧参数） */
  pageSize?: number;
  /** 单页数量 */
  limit?: number;
  /** 搜索关键字 */
  keyword?: string;
  /** 成员状态 */
  status?: string;
  /** 部门节点 ID（后端 /bff/iam-ui/members 使用） */
  departmentId?: string;
}

/**
 * 成员列表响应
 */
export interface MemberListResponse {
  /** 成员数据 */
  members: MemberWithProfile[];
  /** 总数 */
  total: number;
  /** 当前页码 */
  page: number;
  /** 每页数量 */
  limit: number;
}

export interface DepartmentTreeNode {
  id: string;
  name: string;
  children?: DepartmentTreeNode[];
}

export interface MemberListAllParams {
  /** 搜索关键字 */
  keyword?: string;
  /** 成员状态 */
  status?: string;
  /** 部门节点 ID */
  departmentId?: string;
}

type IamUiMember = {
  id?: string;
  userId?: string;
  name?: string;
  nickname?: string;
  username?: string;
  email?: string;
  position?: string;
  status?: string;
  photo?: string;
  avatarUrl?: string;
  departmentId?: string;
  mainDepartmentId?: string;
  departmentUuid?: string;
  deptIds?: string[];
  orgs?: Array<{ id?: string; nodeId?: string; name?: string }>;
};

type IamUiMemberListResponse = {
  list?: IamUiMember[];
  totalElements?: number;
  total?: number;
  pageNumber?: number;
  pageNo?: number;
  pageSize?: number;
};

/**
 * 团队成员信息（来自 /bff/enterprise-members 接口）
 */
export interface TeamMember {
  /** 成员 ID */
  id: string;
  /** 成员名称 */
  name: string;
  /** 邮箱 */
  email: string;
  /** 手机号码 */
  phone?: string;
  /** 头像 URL */
  avatar?: string;
  /** 角色列表 */
  roles: string[];
  /** 最近登录时间 */
  lastLoginTime: string;
  /** 注册时间 */
  registrationTime: string;
  /** 状态 */
  status: 'active' | 'locked';
  /** 部门 */
  department: string;
  /** 授权状态 */
  authorizationStatus: 'joined' | 'pending';
  /** 职位 */
  position?: string;
  /** 员工编号 */
  employeeId?: string;
  /** 是否为主要成员 */
  isPrimary?: boolean;
  /** 加入时间 */
  joinedAt?: string;
  /** 备注 */
  remark?: string;
}

/**
 * 团队成员列表查询参数
 */
export interface TeamMemberListParams {
  /** 页码 */
  page?: number;
  /** 每页数量 */
  limit?: number;
  /** 搜索关键词 */
  search?: string;
  /** 权限组 */
  permissionGroup?: string;
  /** 锁定状态 */
  lockStatus?: string;
  /** 授权状态 */
  authorizationStatus?: string;
  /** 部门 UUID */
  departmentUuid?: string;
}

/**
 * 团队成员列表响应
 */
export interface TeamMemberListResponse {
  /** 成员数据 */
  members: TeamMember[];
  /** 总数 */
  total: number;
  /** 当前页码 */
  page: number;
  /** 每页数量 */
  limit: number;
}

/**
 * 构建成员查询参数
 *
 * @param params - 查询条件
 */
const buildMemberParams = (
  params?: MemberListParams,
): Record<string, string | number> | undefined => {
  if (!params) {
    return undefined;
  }

  const result: Record<string, string | number> = {
    pageNo: params.pageNo ?? params.page ?? 1,
    pageSize: params.pageSize ?? params.limit ?? 10,
  };

  if (params.keyword) {
    result.keyword = params.keyword;
  }
  if (params.status) {
    result.status = params.status;
  }
  if (params.departmentId) {
    result.departmentId = params.departmentId;
  }

  return Object.keys(result).length > 0 ? result : undefined;
};

const toMemberWithProfile = (item: IamUiMember): MemberWithProfile => {
  const memberUuid = item.id || item.userId || '';
  const userUuid = item.userId || item.id || '';
  const displayName =
    item.name || item.nickname || item.username || item.email || memberUuid;

  return {
    member: {
      uuid: memberUuid,
      userUuid,
      name: item.name || item.nickname || item.username,
      displayName,
      position: item.position,
      email: item.email,
      status: item.status,
      departmentId: item.departmentId,
      mainDepartmentId: item.mainDepartmentId,
      departmentUuid: item.departmentUuid,
      deptIds: item.deptIds,
      orgs: item.orgs,
    },
    displayName,
    avatarUrl: item.avatarUrl || item.photo || null,
  };
};

type EnterpriseMember = {
  uuid?: string;
  realName?: string;
  email?: string;
  phone?: string;
  role?: string;
  status?: string;
  enterpriseName?: string;
  position?: string;
  employeeId?: string;
  joinedAt?: string;
  createdAt?: string;
  createTime?: string;
};

type EnterpriseMemberListResponse = {
  data?: EnterpriseMember[];
  total?: number;
  page?: number;
  pageSize?: number;
};

/**
 * 成员相关 API
 */
export const memberApi = {
  getDepartmentTree: () => {
    return request<DepartmentTreeNode[]>('/bff/iam-ui/departments/tree');
  },

  /**
   * 获取成员列表（包含展示信息）
   *
   * @param params - 查询参数
   */
  getMembers: (params?: MemberListParams) => {
    const requestParams = buildMemberParams(params);
    const apiPath = '/bff/iam-ui/members';
    return request<IamUiMemberListResponse>(apiPath, {
      params: requestParams,
    }).then((response) => {
      const list = Array.isArray(response?.list) ? response.list : [];
      const members = list.map(toMemberWithProfile);
      const page = Number(
        response?.pageNumber ?? response?.pageNo ?? requestParams?.pageNo ?? 1,
      );
      const limit = Number(response?.pageSize ?? requestParams?.pageSize ?? 10);
      const total = Number(
        response?.totalElements ?? response?.total ?? members.length,
      );

      return {
        members,
        total,
        page,
        limit,
      } satisfies MemberListResponse;
    });
  },

  /**
   * 获取全量成员列表
   *
   * @param params - 查询参数
   */
  getAllMembers: (
    params?: MemberListAllParams,
  ): Promise<MemberListResponse> => {
    const requestParams: Record<string, string> = {};
    if (params?.keyword) {
      requestParams.keyword = params.keyword;
    }
    if (params?.status) {
      requestParams.status = params.status;
    }
    if (params?.departmentId) {
      requestParams.departmentId = params.departmentId;
    }

    return request<IamUiMemberListResponse & { total?: number }>(
      '/bff/iam-ui/members/all',
      {
        params: requestParams,
      },
    ).then((response) => {
      const list = Array.isArray(response?.list) ? response.list : [];
      const members = list.map(toMemberWithProfile);

      return {
        members,
        total: Number(response?.total ?? members.length),
        page: 1,
        limit: members.length,
      };
    });
  },

  /**
   * 获取团队成员列表
   *
   * @param params - 查询参数
   */
  getTeamMembers: (params?: TeamMemberListParams) => {
    const requestParams: Record<string, string | number> = {
      page: params?.page ?? 1,
      limit: params?.limit ?? 10,
    };

    if (params?.search) {
      requestParams.search = params.search;
    }
    if (params?.permissionGroup) {
      requestParams.role = params.permissionGroup;
    }
    if (params?.lockStatus) {
      requestParams.status = params.lockStatus;
    }
    if (params?.departmentUuid) {
      requestParams.organizationUuid = params.departmentUuid;
    }

    const apiPath = '/bff/enterprise-members';
    return request<EnterpriseMemberListResponse>(apiPath, {
      params: requestParams,
    }).then((response) => {
      const list = Array.isArray(response?.data) ? response.data : [];
      const members: TeamMember[] = list.map((item) => {
        const normalizedStatus = (item.status || '').toLowerCase();
        const status: TeamMember['status'] =
          normalizedStatus === 'active' ? 'active' : 'locked';
        const registrationTime =
          item.createdAt || item.createTime || item.joinedAt || '';

        return {
          id: item.uuid || '',
          name: item.realName || item.email || item.uuid || '',
          email: item.email || '',
          phone: item.phone,
          avatar: undefined,
          roles: item.role ? [item.role] : [],
          lastLoginTime: '',
          registrationTime,
          status,
          department: item.enterpriseName || '',
          authorizationStatus: 'joined',
          position: item.position,
          employeeId: item.employeeId,
          isPrimary: undefined,
          joinedAt: item.joinedAt,
          remark: undefined,
        };
      });

      return {
        members,
        total: Number(response?.total || members.length),
        page: Number(response?.page || requestParams.page || 1),
        limit: Number(response?.pageSize || requestParams.limit || 10),
      } satisfies TeamMemberListResponse;
    });
  },
};
